<template>
    <div class="table-create-record-form">
        <a-form :form="form" @submit="handleSubmit">
            <div class="form-row" v-for="(row, index) in rows" :key="index">
                <div class="form-col" v-for="field in row" :key="field._id" v-if="isValidField(field._id)">
                    <a-form-item :label="field.name">
                        <a-input
                            v-if="field.fieldType === 'text' || field.fieldType === 'long-text'"
                            v-model="record[field._id]"
                        />
                        <a-input-number v-else-if="field.fieldType === 'number'" v-model="record[field._id]" />
                        <a-input v-else-if="field.fieldType === 'currency'" v-model="record[field._id]" />
                        <a-date-picker v-else-if="field.fieldType === 'datetime'" v-model="record[field._id]"       :show-time="defaultTime"
                        />
                        <template v-if="field.fieldType === 'dropdown'">
                            <a-select
                                v-if="!isDynamicDropdown(field)"
                                v-model="record[field._id]"
                                :mode="field.options.dropdown.multiple ? 'multiple' : 'default'"
                            >
                                <a-select-option
                                    v-for="option in field.options.dropdown.options"
                                    :key="option.value"
                                    :value="option.value"
                                >
                                    {{ option.value }}
                                </a-select-option>
                            </a-select>

                            <a-select
                                v-else
                                v-model="record[field._id]"
                                :mode="field.options.dropdown.multiple ? 'multiple' : 'default'"
                                :filterOption="false"
                                :showSearch="true"
                                @search="(value) => handleDynamicSearch(value, field)"
                                :loading="loadingFields[field._id]"
                            >
                                <a-select-option
                                    v-for="option in dynamicOptions[field._id] || []"
                                    :key="option.value"
                                    :value="option.value"
                                >
                                    {{ option.value }}
                                </a-select-option>
                            </a-select>
                        </template>
                        <a-checkbox v-else-if="field.fieldType === 'checkbox'" v-model="record[field._id]" />
                        <a-input v-else-if="field.fieldType === 'url'" v-model="record[field._id]" />
                        <a-input v-else-if="field.fieldType === 'email'" v-model="record[field._id]" />
                        <a-input v-else-if="field.fieldType === 'phone'" v-model="record[field._id]" />
                        <a-input v-else-if="field.fieldType === 'conversation'" v-model="record[field._id]" />

                        <json-cell
                            v-else-if="field.fieldType === 'json'"
                            :record="{ data: record }"
                            :field="field"
                            :outlined="true"
                            @update-record="handleJsonUpdate"
                        />
                        <custom-picker
                            v-else-if="field.fieldType === 'user'"
                            :items="getAgentsOptions()"
                            label-key="label"
                            id-key="id"
                            :title="$t('message.selectAgent')"
                            v-model="record[field._id]"
                            icon="support_agent"
                        />

                        <div
                            class="asset-upload"
                            v-else-if="['file', 'image', 'video', 'audio'].includes(field.fieldType)"
                        >
                            <div class="asset-preview" v-if="record[field._id]">
                                <img
                                    v-if="field.fieldType === 'image'"
                                    :src="getAssetUrl(record[field._id])"
                                    alt="Resim Önizleme"
                                />
                                <video
                                    v-else-if="field.fieldType === 'video'"
                                    :src="getAssetUrl(record[field._id])"
                                    controls
                                ></video>
                                <audio
                                    v-else-if="field.fieldType === 'audio'"
                                    :src="getAssetUrl(record[field._id])"
                                    controls
                                ></audio>
                                <div v-else>{{ getAssetName(record[field._id]) }}</div>
                            </div>
                            <a-upload
                                :before-upload="(file) => beforeUpload(file, field._id)"
                                :custom-request="(args) => uploadFile(args, field._id)"
                                :show-upload-list="false"
                                :accept="getAcceptedFileTypes(field.fieldType)"
                                v-else
                            >
                                <a-button>
                                    <a-icon type="upload" />
                                    {{ getUploadButtonText(field.fieldType) }}
                                </a-button>
                            </a-upload>
                        </div>
                        <color-picker v-else-if="field.fieldType === 'color'" v-model="record[field._id]" />

                        <div v-else-if="field.fieldType === 'contact'" class="contact-field">
                            <div
                                class="contact-wrapper"
                                @mouseenter="activeContactField = field._id"
                                @mouseleave="handleContactMouseLeave"
                            >
                                <div
                                    v-if="record[field._id]"
                                    class="contact-info"
                                    @click="
                                        isReadonlyField(field) ? $_.noop : viewContactProfile(getContact(field._id))
                                    "
                                >
                                    <div class="contact-avatar">
                                        <PlatformIconViewer :platform="$_.get(getContact(field._id), 'platform')" />
                                    </div>
                                    <span class="contact-name">{{ $_.get(getContact(field._id), "fullname") }}</span>
                                </div>
                                <a v-else @click="showContactPicker(field._id)">+ Kişi Ekle</a>

                                <div
                                    class="contact-actions"
                                    v-if="
                                        activeContactField === field._id && record[field._id] && !isReadonlyField(field)
                                    "
                                >
                                    <SupsisButton icon="edit" @click="showContactPicker(field._id)" xxsmall />
                                    <SupsisButton icon="delete" @click="removeContact(field._id)" xxsmall />
                                </div>
                            </div>
                        </div>

                        <div v-else-if="field.fieldType === 'linked-record'" class="linked-record-field">
                            <div
                                class="linked-record-wrapper"
                                @mouseenter="activeLinkedRecordField = field._id"
                                @mouseleave="handleLinkedRecordMouseLeave"
                            >
                                <div
                                    v-if="record[field._id]"
                                    class="linked-record-info"
                                    @click="viewLinkedRecord(field, getLinkedRecord(field._id))"
                                >
                                    <i class="material-icons">switch_access_shortcut</i>
                                    <span class="linked-record-label">{{ getLinkedRecordLabel(field._id) }}</span>
                                </div>
                                <a v-else @click="showRecordPicker(field._id)">+ Kayıt Ekle</a>

                                <div
                                    class="linked-record-actions"
                                    v-if="
                                        activeLinkedRecordField === field._id &&
                                        record[field._id] &&
                                        !isReadonlyField(field)
                                    "
                                >
                                    <SupsisButton icon="edit" @click="showRecordPicker(field._id)" xxsmall />
                                    <SupsisButton icon="delete" @click="removeLinkedRecord(field._id)" xxsmall />
                                </div>
                            </div>
                        </div>

                        <div v-else-if="field.fieldType === 'task'" class="task-field">
                            <div
                                class="task-wrapper"
                                @mouseenter="activeTaskField = field._id"
                                @mouseleave="handleTaskMouseLeave"
                            >
                                <div
                                    v-if="record[field._id]"
                                    class="task-info"
                                    @click="viewTask(getTask(field._id))"
                                >
                                    <FieldIcon type="task" :size="16" class="task-icon" :default-icon="field.icon" />
                                    <span class="task-title">{{ $_.get(getTask(field._id), "title") }}</span>
                                </div>
                                <a v-else @click="showTaskPicker(field._id)">+ Task Ekle</a>

                                <div
                                    class="task-actions"
                                    v-if="
                                        activeTaskField === field._id &&
                                        record[field._id] &&
                                        !isReadonlyField(field)
                                    "
                                >
                                    <SupsisButton icon="edit" @click="showTaskPicker(field._id)" xxsmall />
                                    <SupsisButton icon="delete" @click="removeTask(field._id)" xxsmall />
                                </div>
                            </div>
                        </div>
                    </a-form-item>
                </div>
            </div>
            <a-form-item>
                <a-button type="primary" html-type="submit" :loading="loading">Kaydet</a-button>
            </a-form-item>
        </a-form>

        <Modal2
            v-if="showContactModal"
            @cancel="closeContactModal"
            :title="'Kişi Seç'"
            :okText="'Kaydet'"
            xlarge
            className="contact-picker-modal keydown-prevent-modal"
        >
            <List picker-mode @select="onContactSelected" />
        </Modal2>

        <Modal2
            v-if="showRecordPickerModal"
            @cancel="closeRecordPickerModal"
            :title="'Kayıt Seç'"
            :okText="'Kaydet'"
            xlarge
            className="record-picker-modal keydown-prevent-modal"
        >
            <RecordPicker
                :table-id="activeLinkedRecordTableId"
                :pickerMode="true"
                @record-selected="onRecordSelected"
                :labelFieldId="activeLinkedRecordLabelField"
            />
        </Modal2>

        <Modal2
            v-if="showTaskPickerModal"
            @cancel="closeTaskPickerModal"
            :title="'Task Seç'"
            :okText="'Kaydet'"
            xlarge
            className="task-picker-modal keydown-prevent-modal"
        >
            <TaskListView picker-mode @selected="onTaskSelected" />
        </Modal2>
    </div>
</template>

<script>
import Vue from "vue";
import { TABLE_API } from "../../../../api/table";
import { uploadAsset } from "../../../../api/assetUpload";
import axios from "axios";
import CustomPicker from "../../../../components/pickers/CustomPicker.vue";
import { mapState } from "vuex";
import Modal2 from "../../../../components/common/Modal2.vue";
import List from "../../Visitors/List.vue";
import PlatformIconViewer from "../../../../components/common/PlatformIconViewer.vue";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import { mapActions } from "vuex";
import log2 from "../../../../utils/log2";
import RecordPicker from "./pickers/RecordPicker.vue";
import JsonCell from "./cells/JsonCell.vue";
import TaskListView from "../../../../components/task/TaskListView.vue";
import FieldIcon from "../FieldIcon.vue";

const log = log2("TableCreateRecordForm");
export default Vue.extend({
    name: "TableCreateRecordForm",
    components: {
        CustomPicker,
        Modal2,
        List,
        PlatformIconViewer,
        SupsisButton,
        RecordPicker,
        JsonCell,
        TaskListView,
        FieldIcon,
    },
    props: {
        table: {
            type: Object,
            required: true,
        },
        predefinedValues: {
            type: Object,
            default: () => ({}),
        },
        // uzaktan gönderilen tablo record'laırnı cachelemek için kullanlır
        defaultLinkedRecordCache: {
            type: Object,
        },
    },
    data() {
        return {
            loading: false,
            defaultTime: {
                defaultValue: this.$moment("00:00:00", "HH:mm:ss"),
            },
            form: this.$form.createForm(this),
            record: {},
            uploadedAssets: {},
            showContactModal: false,
            activeFieldId: null,
            contactCache: {},
            activeContactField: null,
            hideContactActionsTimeout: null,
            dynamicOptions: {},
            loadingFields: {},
            showRecordPickerModal: false,
            activeLinkedRecordField: null,
            activeLinkedRecordTableId: null,
            activeLinkedRecordLabelField: null,
            linkedRecordCache: {},
            showTaskPickerModal: false,
            activeTaskField: null,
            taskCache: {},
        };
    },
    computed: {
        ...mapState({
            users: (state) => state.user.users.data,
        }),
        rows() {
            const rows = [];
            let currentRow = [];

            this.table.fields.forEach((field) => {
                if (this.isValidField(field._id)) {
                    currentRow.push(field);
                    if (currentRow.length === 2) {
                        rows.push(currentRow);
                        currentRow = [];
                    }
                }
            });

            if (currentRow.length > 0) {
                rows.push(currentRow);
            }

            return rows;
        },
    },
    methods: {
        ...mapActions({
            getVisitor: "visitor/getVisitor",
        }),

        isValidField(fieldId) {
            const field = this.table.fields.find((f) => f._id === fieldId);
            //invalid field types: ai , automation-button , form-submission
            return (
                !["ai", "automation-button", "form-submission", "formula", "lookup", "auto-increment"].includes(
                    field.fieldType
                ) && !field.fieldType.startsWith("record_")
            );
        },
        beforeUpload(file, fieldId) {
            const fieldType = this.table.fields.find((f) => f._id === fieldId).fieldType;
            const isValidType = file.type.indexOf(fieldType) !== -1 || fieldType === "file";
            if (!isValidType) {
                this.$message.error("Geçersiz dosya türü");
            }
            return isValidType;
        },
        async uploadFile({ file, onSuccess, onError }, fieldId) {
            console.log("uploadFile", {
                file,
                onSuccess,
                onError,
                fieldId,
            });
            const siteId = this.$store.state.site.site._id;
            const path = `${siteId}/table-assets/`;
            try {
                const uploadData = await uploadAsset({
                    path,
                    name: file.name,
                    size: file.size,
                    type: "table-asset",
                    mime: file.type,
                    ext: file.name.split(".").pop(),
                });
                await axios({
                    method: uploadData.data.method,
                    url: uploadData.data.url,
                    data: file,
                    headers: { "Content-Type": "application/octet-stream" },
                });
                this.uploadedAssets[fieldId] = uploadData.data.asset;
                this.record[fieldId] = uploadData.data.asset;
                this.record = _.cloneDeep(this.record);
                onSuccess();
            } catch (error) {
                console.error("Asset yükleme hatası:", error);
                onError();
            }
        },
        async handleSubmit(e) {
            e.preventDefault();

            // Formu doğrula
            this.form.validateFields(async (err, values) => {
                if (!err) {
                    try {
                        this.loading = true;
                        // Yeni kayıt için değerleri düzenle
                        const recordData = { ...this.record };
                        Object.keys(recordData).forEach((fieldId) => {
                            if (fieldId === "_id") return;
                            const field = this.table.fields.find((f) => f._id === fieldId);
                            const isAsset = ["file", "image", "video", "audio"].includes(field.fieldType);
                            if (isAsset) {
                                recordData[fieldId] = recordData[fieldId]._id;
                            }
                        });

                        // Yeni kaydı oluştur
                        const newRecord = await TABLE_API.createRecordMulti(this.table._id, [recordData]);

                        // Üst bileşene yeni kaydın oluşturulduğunu bildir
                        this.$emit("record-created", newRecord[0]);

                        // Formu sıfırla
                        this.form.resetFields();
                        this.record = {};

                        // Başarılı mesajı göster
                        this.$message.success("Kayıt başarıyla oluşturuldu");
                    } catch (error) {
                        console.error("Kayıt oluşturulurken hata oluştu:", error);
                        // Hata mesajını göster
                        this.$message.error("Kayıt oluşturulurken bir hata oluştu");
                    } finally {
                        this.loading = false;
                    }
                }
            });
        },
        getAcceptedFileTypes(fieldType) {
            const fileTypes = {
                file: "*",
                video: "video/*",
                image: "image/*",
                audio: "audio/*",
            };
            return fileTypes[fieldType] || "*";
        },
        getUploadButtonText(fieldType) {
            const buttonText = {
                file: "Dosya Yükle",
                video: "Video Yükle",
                image: "Resim Yükle",
                audio: "Ses Dosyası Yükle",
            };
            return buttonText[fieldType] || "Dosya Yükle";
        },
        getAssetUrl(asset) {
            return asset?.cdnUrl;
        },
        getAssetName(asset) {
            return asset.name;
        },
        getAgentsOptions() {
            let items = this.users || [];
            items = items.map((item) => ({
                id: item._id,
                label: item.name,
            }));

            items = [
                {
                    id: null,
                    label: this.$t("message.allUsers"),
                },
                ...items,
            ];

            return items;
        },
        showContactPicker(fieldId) {
            this.activeFieldId = fieldId;
            this.showContactModal = true;
        },

        closeContactModal() {
            this.showContactModal = false;
            this.activeFieldId = null;
        },

        onContactSelected(contact) {
            if (this.activeFieldId) {
                this.record[this.activeFieldId] = contact._id;
                this.contactCache[this.activeFieldId] = contact;
                this.closeContactModal();
            }
        },

        getContact(fieldId) {
            const contactId = this.record[fieldId];

            let result = null;
            if (typeof contactId === "string") {
                result = this.contactCache[contactId] || null;
            }
            log("getContact", {
                contactId,
                fieldId,
                record: this.record,
                contactCache: this.contactCache,
                contact: this.contactCache[fieldId],
                result,
            });
            return result;
        },

        viewContactProfile(contact) {
            if (contact) {
                this.$store.dispatch("app/addNewWindow", {
                    type: "show-visitor",
                    title: contact.fullname,
                    payload: {
                        visitorId: contact._id,
                    },
                    footer: false,
                    autoClose: false,
                });
            }
        },

        handleContactMouseLeave() {
            this.hideContactActionsTimeout = setTimeout(() => {
                this.activeContactField = null;
            }, 200);
        },

        removeContact(fieldId) {
            this.record[fieldId] = null;
            this.$message.success("Kişi başarıyla kaldırıldı");
        },
        async bindPredefinedValues(predefinedValues) {
            for await (const key of Object.keys(predefinedValues || {})) {
                const isContactField = this.table.fields.find((f) => f._id === key)?.fieldType === "contact";
                log("bindPredefinedValues", {
                    isContactField,
                    key,
                    predefinedValues,
                });
                if (isContactField) {
                    try {
                        const res = await this.getVisitor(predefinedValues[key]);
                        this.contactCache[predefinedValues[key]] = res.data;
                    } catch (error) {
                        console.error(error);
                    }
                }
                this.record[key] = predefinedValues[key];
                log("bindPredefinedValues after", {
                    record: this.record,
                });
                setTimeout(() => {
                    this.record = this.$_.cloneDeep(this.record);
                }, 500);
            }
        },
        isReadonlyField(field) {
            return Object.keys(this.predefinedValues).includes(field._id);
        },
        isDynamicDropdown(field) {
            return field.options?.dropdown?.mode === "dynamic";
        },

        handleDynamicSearch: _.debounce(async function (value, field) {
            if (!this.isDynamicDropdown(field)) return;

            const fieldId = field._id;
            this.$set(this.loadingFields, fieldId, true);

            try {
                const remoteTableId = field.options.dropdown.remoteTable;
                const remoteTableField = field.options.dropdown.remoteTableField;

                const results = await TABLE_API.searchFieldValues(remoteTableId, remoteTableField, value || "");

                this.$set(
                    this.dynamicOptions,
                    fieldId,
                    results.map((item) => ({
                        value: item,
                        color: this.generateColorFromText(item),
                    }))
                );
            } catch (error) {
                console.error("Arama hatası:", error);
                this.$message.error(this.$t("tables.searchError"));
            } finally {
                this.$set(this.loadingFields, fieldId, false);
            }
        }, 300),

        generateColorFromText(text) {
            let hash = 0;
            for (let i = 0; i < text.length; i++) {
                hash = text.charCodeAt(i) + ((hash << 5) - hash);
            }

            const hue = Math.abs(hash) % 360;
            const saturation = 25 + (Math.abs(hash) % 15);
            const lightness = 85 + (Math.abs(hash) % 10);

            return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
        },
        async initializeDynamicDropdowns() {
            const dynamicFields = this.table.fields.filter((field) => this.isDynamicDropdown(field));

            for (const field of dynamicFields) {
                await this.handleDynamicSearch("", field);
            }
        },
        showRecordPicker(fieldId) {
            const field = this.table.fields.find((f) => f._id === fieldId);
            console.log("showRecordPicker", {
                field,
                fieldId,
            });
            this.activeLinkedRecordField = fieldId;
            this.activeLinkedRecordTableId = field.options.linkedRecord.table;
            this.activeLinkedRecordLabelField = field.options.linkedRecord.labelField;
            this.showRecordPickerModal = true;
        },

        closeRecordPickerModal() {
            this.showRecordPickerModal = false;
            this.activeLinkedRecordField = null;
            this.activeLinkedRecordTableId = null;
            this.activeLinkedRecordLabelField = null;
        },

        onRecordSelected(selectedRecord) {
            console.log("selectedRecord", {
                selectedRecord,
                activeLinkedRecordField: this.activeLinkedRecordField,
                record: this.record,
                linkedRecordCache: this.linkedRecordCache,
            });
            if (this.activeLinkedRecordField) {
                this.record[this.activeLinkedRecordField] = selectedRecord._id;
                this.linkedRecordCache[selectedRecord._id] = selectedRecord;
                this.closeRecordPickerModal();
            }
        },

        getLinkedRecord(fieldId) {
            const recordId = this.record[fieldId];
            console.log("getLinkedRecord", {
                fieldId,
                recordId,
            });
            return this.linkedRecordCache[recordId] || null;
        },

        getLinkedRecordLabel(fieldId) {
            const record = this.getLinkedRecord(fieldId);
            console.log("getLinkedRecordLabel", { record, fieldId });
            if (!record) return "";

            const field = this.table.fields.find((f) => f._id === fieldId);
            const labelField = field.options.linkedRecord.labelField;
            return this.$_.get(record, `data.${labelField}`, "Bağlı Kayıt");
        },

        viewLinkedRecord(field, linkedRecord) {
            if (linkedRecord && !this.isReadonlyField(field)) {
                this.$store.dispatch("app/addNewWindow", {
                    id: linkedRecord._id,
                    type: "table-record-viewer",
                    title: `${field.name} - Record Viewer`,
                    payload: {
                        tableId: field.options.linkedRecord.table,
                        recordId: linkedRecord._id,
                    },
                    multiple: true,
                    autoClose: false,
                });
            }
        },

        handleLinkedRecordMouseLeave() {
            setTimeout(() => {
                if (this.showRecordPickerModal) return;
                this.activeLinkedRecordField = null;
            }, 200);
        },

        removeLinkedRecord(fieldId) {
            this.record[fieldId] = null;
            this.$message.success("Bağlantılı kayıt başarıyla kaldırıldı");
        },
        handleJsonUpdate({ fieldId, value }) {
            this.$set(this.record, fieldId, value);
        },

        // Task ile ilgili methods
        showTaskPicker(fieldId) {
            this.activeTaskField = fieldId;
            this.showTaskPickerModal = true;
        },

        closeTaskPickerModal() {
            this.showTaskPickerModal = false;
            this.activeTaskField = null;
        },

        onTaskSelected(task) {
            if (this.activeTaskField) {
                this.record[this.activeTaskField] = task._id;
                this.taskCache[task._id] = task;
                this.closeTaskPickerModal();
            }
        },

        getTask(fieldId) {
            const taskId = this.record[fieldId];
            if (typeof taskId === "string") {
                return this.taskCache[taskId] || null;
            }
            return taskId || null;
        },

        viewTask(task) {
            if (task) {
                this.$store.dispatch("app/addNewWindow", {
                    type: "editor-task",
                    title: task.title,
                    payload: {
                        taskId: task._id,
                    },
                    autoClose: false,
                });
            }
        },

        handleTaskMouseLeave() {
            setTimeout(() => {
                if (this.showTaskPickerModal) return;
                this.activeTaskField = null;
            }, 200);
        },

        removeTask(fieldId) {
            this.record[fieldId] = null;
            this.$message.success("Task başarıyla kaldırıldı");
        },
    },
    mounted() {},
    created() {
        this.linkedRecordCache = this.defaultLinkedRecordCache || {};
        this.bindPredefinedValues(this.predefinedValues);
        this.initializeDynamicDropdowns();
    },
});
</script>

<style scoped lang="scss">
.table-create-record-form {
    .form-row {
        display: flex;
        margin-bottom: 16px;

        .form-col {
            flex: 1;
            margin-right: 16px;

            &:last-child {
                margin-right: 0;
            }
        }
    }

    .ant-form-item {
        margin-bottom: 0;
    }
}

.asset-upload {
    display: flex;
    align-items: center;

    .asset-preview {
        margin-left: 16px;

        img,
        video,
        audio {
            max-width: 200px;
            max-height: 200px;
        }
    }
}

.contact-field {
    .contact-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        width: 100%;

        .contact-info {
            display: flex;
            align-items: center;
            gap: 8px;
            cursor: pointer;
            padding: 4px;
            border-radius: 4px;
            transition: background-color 0.2s ease;

            &:hover {
                background: var(--color-bg-neutral-hover);
            }

            .contact-avatar {
                width: 32px;
                height: 32px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 50%;
                background: var(--color-bg-neutral-hover);
            }

            .contact-name {
                font-weight: 500;
            }
        }

        .contact-actions {
            position: absolute;
            right: -14px;
            top: 50%;
            transform: translateY(-50%);
            transition: transform 0.3s ease;
            z-index: 10;
            height: 100%;
            width: max-content;
            background: #0000002b;
            display: flex;
            justify-content: flex-end;
            gap: 5px;
            align-items: center;
            padding: 0 10px;
        }
    }
}

.linked-record-field {
    .linked-record-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        width: 100%;

        .linked-record-info {
            display: flex;
            align-items: center;
            gap: 8px;
            cursor: pointer;
            padding: 4px;
            border-radius: 4px;
            transition: background-color 0.2s ease;

            &:hover {
                background: var(--color-bg-neutral-hover);
            }

            .linked-record-label {
                font-weight: 500;
            }
        }

        .linked-record-actions {
            position: absolute;
            right: -14px;
            top: 50%;
            transform: translateY(-50%);
            z-index: 10;
            height: 100%;
            width: max-content;
            background: #0000002b;
            display: flex;
            justify-content: flex-end;
            gap: 5px;
            align-items: center;
            padding: 0 10px;
        }
    }
}

.task-field {
    .task-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        width: 100%;

        .task-info {
            display: flex;
            align-items: center;
            gap: 4px;
            cursor: pointer;
            padding: 4px;
            border-radius: 4px;
            transition: background-color 0.2s ease;

            &:hover {
                background: var(--color-bg-neutral-hover);
            }

            .task-title {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                font-weight: 500;
            }
        }

        .task-actions {
            position: absolute;
            right: -14px;
            top: 50%;
            transform: translateY(-50%);
            z-index: 10;
            height: 100%;
            width: max-content;
            background: #0000002b;
            display: flex;
            justify-content: flex-end;
            gap: 5px;
            align-items: center;
            padding: 0 10px;
        }
    }
}
</style>

<style lang="scss">
.record-picker-modal {
    .table-body {
        max-height: calc(100vh - 450px);
        height: calc(100vh - 450px);
    }
}
</style>
